<div class="ob-page kf-page" [class.ob-page--public]="isPublic()">
	<!-- Public mode wears a sober brand header instead of the app sidebar (M02 §6). -->
	@if (isPublic()) {
		<header class="ob-brand">
			<!-- El logotipo oficial: monograma + nombre. Hasta el 27/08 aquí presidía
			     el alta pública el imagotipo de OTRA marca, heredado del fork; sus
			     ficheros ya no están en el repositorio. -->
			<img class="ob-brand__mark" src="assets/layout/images/branding/km-monogram.png" alt="" />
			<img class="ob-brand__logo" src="assets/layout/images/branding/kairos-merit-wordmark-black.png" alt="Kairos Merit" />
		</header>
	}

	<div class="ob-shell">
		@if (loading()) {
			<app-spinner class="screen-loader" [loading]="true" />
		} @else if (catalogsFailed()) {
			<!-- Sin catálogos no hay formulario que rellenar: los desplegables salen
			     vacíos y el candidato no tiene forma de saber por qué. -->
			<section class="qs-card ob-state">
				<lucide-icon name="ShieldAlert" [size]="30"></lucide-icon>
				<h1>{{ 'merit.onboarding.errors.title' | transloco }}</h1>
				<p>{{ 'merit.onboarding.errors.catalogs' | transloco }}</p>
				<button type="button" class="qs-btn qs-btn--ghost" (click)="retry()">
					{{ 'merit.onboarding.actions.retry' | transloco }}
				</button>
			</section>
		} @else if (loadError(); as message) {
			<section class="qs-card ob-state">
				<lucide-icon name="ShieldAlert" [size]="30"></lucide-icon>
				<h1>{{ 'merit.onboarding.errors.title' | transloco }}</h1>
				<p>{{ message }}</p>
				<button type="button" class="qs-btn qs-btn--ghost" (click)="retry()">
					{{ 'merit.onboarding.actions.retry' | transloco }}
				</button>
			</section>
		} @else if (view() === 'done' || reviewing()) {
			<section class="qs-card ob-state ob-state--ok">
				<span class="ob-state__check"><lucide-icon name="Check" [size]="26"></lucide-icon></span>
				@if (unlocked()) {
					<!-- A migrated candidate (10/09): this submit opened the portal, so
					     "the team will review" would be false. -->
					<h1>{{ 'merit.onboarding.success.unlockedTitle' | transloco }}</h1>
					<p>{{ 'merit.onboarding.success.unlockedMessage' | transloco }}</p>
					<a class="qs-btn qs-btn--primary" routerLink="/candidate/home">
						{{ 'merit.onboarding.success.goToPlatform' | transloco }}
					</a>
				} @else if (reviewing()) {
					<!-- 4.1 (11/09): a migrated candidate who changed imported data. The
					     portal stays closed until the team decides in Scoring. -->
					<h1>{{ 'merit.onboarding.success.reviewTitle' | transloco }}</h1>
					<p>{{ 'merit.onboarding.success.reviewMessage' | transloco }}</p>
				} @else {
					<h1>{{ 'merit.onboarding.success.title' | transloco }}</h1>
					<p>{{ 'merit.onboarding.success.message' | transloco }}</p>
					@if (!isPublic()) {
						<a class="qs-btn qs-btn--primary" routerLink="/candidate/profile">
							{{ 'merit.onboarding.success.goToProfile' | transloco }}
						</a>
					}
				}
			</section>
		} @else if (state(); as data) {
			<div class="kf-layout">
				<!-- Ten-step progress + jump index. -->
				<nav class="kf-rail" [attr.aria-label]="'merit.onboarding.progress.label' | transloco">
					<div class="rail-progress">
						<div class="rail-progress__head">
							<span class="rail-progress__title">{{ 'merit.onboarding.progress.label' | transloco }}</span>
							<span class="rail-progress__value">{{ progress() }}%</span>
						</div>
						<div
							class="ob-track"
							role="progressbar"
							[attr.aria-valuenow]="progress()"
							aria-valuemin="0"
							aria-valuemax="100"
						>
							@for (s of steps; track s; let i = $index) {
								<span
									class="ob-track__cell"
									[class.is-done]="completed().has(s)"
									[class.is-missing]="showMissing() && missingSteps().has(s)"
									[class.is-current]="view() === 'wizard' && i === index()"
								></span>
							}
						</div>
					</div>

					<div class="rail-links">
						@for (s of steps; track s; let i = $index) {
							<button
								type="button"
								class="rail-link ob-steplink"
								[class.is-active]="view() === 'wizard' && i === index()"
								[class.is-locked]="signupPending() && i > 0"
								(click)="goTo(i)"
							>
								<span class="ob-steplink__num">{{ pad(i + 1) }}</span>
								<span class="ob-steplink__label">
									{{ 'merit.onboarding.steps.' + s + '.title' | transloco }}
								</span>
								@if (showMissing() && missingSteps().has(s)) {
									<!-- Red dot: this step is what «Something is missing» refers to. -->
									<span
										class="ob-steplink__alert"
										role="img"
										[attr.aria-label]="'merit.onboarding.progress.missing' | transloco"
										[title]="'merit.onboarding.progress.missing' | transloco"
									></span>
								} @else if (completed().has(s)) {
									<lucide-icon class="ob-steplink__check" name="Check" [size]="13"></lucide-icon>
								}
							</button>
						}
						<button
							type="button"
							class="rail-link ob-steplink ob-steplink--review"
							[class.is-active]="view() === 'review'"
							(click)="openReview()"
						>
							<span class="ob-steplink__num"><lucide-icon name="Send" [size]="13"></lucide-icon></span>
							<span class="ob-steplink__label">{{ 'merit.onboarding.review.title' | transloco }}</span>
						</button>
					</div>

					<p class="rail-note">{{ 'merit.onboarding.progress.note' | transloco }}</p>
				</nav>

				<div class="kf-form">
					<!-- What the team asked this candidate to fix (27/10), until they send it back. -->
					@if (data.correctionRequest; as request) {
						<section class="ob-corrections" role="note">
							<h2 class="ob-corrections__title">{{ 'merit.onboarding.corrections.title' | transloco }}</h2>
							<p class="ob-corrections__intro">{{ 'merit.onboarding.corrections.intro' | transloco }}</p>
							<p class="ob-corrections__text">{{ request.message }}</p>
						</section>
					}
					@if (view() === 'review') {
						<app-onboarding-review
							[state]="data"
							[submitting]="submitting()"
							[error]="submitError()"
							(edit)="goTo($event)"
							(send)="submit()"
						></app-onboarding-review>
					} @else {
						<form [formGroup]="form" (ngSubmit)="next()" novalidate>
							@if (stepError(); as message) {
								<p class="qs-alert" role="alert">
									<i class="pi pi-exclamation-circle" aria-hidden="true"></i>
									<span>{{ message }}</span>
								</p>
							}

							<section class="qs-card">
								<header class="qs-head">
									<span class="ob-index">{{ pad(index() + 1) }}</span>
									<div class="qs-headings">
										<h1 class="qs-title ob-title">
											{{ 'merit.onboarding.steps.' + step() + '.title' | transloco }}
										</h1>
										<p class="qs-hint">
											{{ 'merit.onboarding.steps.' + step() + '.hint' | transloco }}
										</p>
									</div>
								</header>

								@switch (step()) {
									@case ('personal') {
										<div class="qs-grid" formGroupName="personal">
											<div class="qs-field qs-col-6">
												<label class="qs-label" for="ob-fullName">
													{{ 'merit.onboarding.fields.fullName' | transloco }}<span class="qs-req">*</span>
													@if (isSuggested('personal.fullName')) {
														<span class="cv-tag">{{ 'merit.onboarding.cv.suggested' | transloco }}</span>
													}
												</label>
												<input
													id="ob-fullName"
													class="qs-input"
													[class.is-invalid]="showErr('personal.fullName')"
													formControlName="fullName"
												/>
											</div>
											<div class="qs-field qs-col-6">
												<label class="qs-label" for="ob-email">
													{{ 'merit.onboarding.fields.email' | transloco }}<span class="qs-req">*</span>
													@if (isSuggested('personal.email')) {
														<span class="cv-tag">{{ 'merit.onboarding.cv.suggested' | transloco }}</span>
													}
												</label>
												<input
													id="ob-email"
													type="email"
													class="qs-input"
													[class.is-invalid]="showErr('personal.email')"
													formControlName="email"
												/>
											</div>
											<div class="qs-field qs-col-4">
												<label class="qs-label" for="ob-phone">
													{{ 'merit.onboarding.fields.phone' | transloco }}<span class="qs-req">*</span>
													@if (isSuggested('personal.phone')) {
														<span class="cv-tag">{{ 'merit.onboarding.cv.suggested' | transloco }}</span>
													}
												</label>
												<input
													id="ob-phone"
													type="tel"
													class="qs-input"
													[class.is-invalid]="showErr('personal.phone')"
													formControlName="phone"
													placeholder="+971…"
												/>
											</div>
											<div class="qs-field qs-col-4">
												<label class="qs-label" for="ob-birthDate">
													{{ 'merit.onboarding.fields.birthDate' | transloco }}<span class="qs-req">*</span>
													@if (isSuggested('personal.birthDate')) {
														<span class="cv-tag">{{ 'merit.onboarding.cv.suggested' | transloco }}</span>
													}
												</label>
												<input
													id="ob-birthDate"
													type="date"
													class="qs-input"
													[class.is-invalid]="showErr('personal.birthDate')"
													formControlName="birthDate"
												/>
											</div>
											<!--
												Nacionalidad en cascada continente → país. Sustituye al desplegable
												contra el catálogo `nationalities`: lo que se guarda ahora es el
												código ISO, no un nombre que dependa del idioma.
											-->
											<app-country-select
												formControlName="countryCode"
												countryLabelKey="common.countrySelect.countryOfBirth"
												continentFieldClass="qs-field qs-col-4"
												countryFieldClass="qs-field qs-col-4"
												[error]="showErr('personal.countryCode')"
												[appendTo]="null"
												[badge]="
													isSuggested('personal.countryCode')
														? ('merit.onboarding.cv.suggested' | transloco)
														: null
												"
											></app-country-select>
											<div class="qs-field qs-col-6">
												<label class="qs-label">
													{{ 'merit.onboarding.fields.city' | transloco }}<span class="qs-req">*</span>
													@if (isSuggested('personal.city')) {
														<span class="cv-tag">{{ 'merit.onboarding.cv.suggested' | transloco }}</span>
													}
												</label>
												<app-select
													[options]="cityOptions"
													formControlName="city"
													[error]="showErr('personal.city')"
													[filter]="true"
													[appendTo]="null"
													size="medium"
													[placeholder]="'merit.onboarding.placeholders.select' | transloco"
												></app-select>
												<!-- Fuera de la lista (Europa incluida desde el 04/09): se escribe
												     ciudad y país, y eso es lo que se guarda. -->
												@if (form.controls.personal.controls.city.value === 'Other') {
													<input
														type="text"
														class="qs-input"
														formControlName="cityOther"
														[placeholder]="'merit.onboarding.fields.cityOther' | transloco" />
												}
											</div>
											<div class="qs-field qs-col-6">
												<label class="qs-label" for="ob-linkedin">
													{{ 'merit.onboarding.fields.linkedinUrl' | transloco }}<span class="qs-req">*</span>
													@if (isSuggested('personal.linkedinUrl')) {
														<span class="cv-tag">{{ 'merit.onboarding.cv.suggested' | transloco }}</span>
													}
												</label>
												<input
													id="ob-linkedin"
													class="qs-input"
													[class.is-invalid]="showErr('personal.linkedinUrl')"
													formControlName="linkedinUrl"
													placeholder="https://linkedin.com/in/…"
												/>
												<small class="qs-hint">{{ 'merit.onboarding.fields.linkedinUrlHint' | transloco }}</small>
											</div>
											<!-- Altura (Jumeirah 13-ago). Obligatoria desde el 02/09, con su nota: hay
											     que decir para qué se pide, y sobre todo que no
											     descarta — un campo físico sin explicar en un
											     formulario de empleo se lee como un filtro. -->
											<div class="qs-field qs-col-6">
												<label class="qs-label" for="ob-height">
													{{ 'merit.onboarding.fields.heightCm' | transloco }}<span class="qs-req">*</span>
													@if (isSuggested('personal.heightCm')) {
														<span class="cv-tag">{{ 'merit.onboarding.cv.suggested' | transloco }}</span>
													}
												</label>
												<input
													id="ob-height"
													class="qs-input"
													[class.is-invalid]="showErr('personal.heightCm')"
													type="number"
													min="100"
													max="250"
													formControlName="heightCm"
													[placeholder]="'merit.onboarding.placeholders.heightCm' | transloco"
												/>
												<small class="qs-hint">{{ 'merit.onboarding.fields.heightCmHint' | transloco }}</small>
											</div>
										</div>
									}

									@case ('documents') {
										<!-- Alta pública sin empezar (23/09): el email y la privacidad van
										     ANTES de cualquier fichero — sin la casilla no se guarda nada
										     (PDPL). El primer fichero abre el borrador. -->
										@if (signupPending()) {
											<div class="ob-start" [formGroup]="startForm">
												<p class="ob-start__intro">{{ 'merit.onboarding.signup.intro' | transloco }}</p>
												<div class="qs-grid">
													<div class="qs-field qs-col-6">
														<label class="qs-label" for="ob-start-email">
															{{ 'merit.onboarding.signup.email' | transloco }}<span class="qs-req">*</span>
														</label>
														<input
															id="ob-start-email"
															type="email"
															class="qs-input"
															autocomplete="email"
															[class.is-invalid]="showStartErr('email')"
															formControlName="email"
														/>
													</div>
												</div>
												<!-- Honeypot: fuera de la vista, solo lo rellena un bot. -->
												<input
													class="ob-start__hp"
													type="text"
													tabindex="-1"
													autocomplete="off"
													aria-hidden="true"
													formControlName="website"
												/>
												<div class="qs-consent">
													<input id="ob-start-privacy" class="qs-check" type="checkbox" formControlName="privacy" />
													<div class="qs-consent__body">
														<label for="ob-start-privacy">{{ 'merit.onboarding.signup.privacy' | transloco }}</label>
														<a class="qs-legal-link" href="/legal" target="_blank" rel="noopener">
															{{ 'merit.onboarding.signup.privacyLink' | transloco }}
														</a>
														@if (showStartErr('privacy')) {
															<small class="qs-err">{{ 'merit.onboarding.signup.privacyRequired' | transloco }}</small>
														}
													</div>
												</div>
											</div>
										}
										@if (startError(); as message) {
											<p class="qs-alert" role="alert">
												<i class="pi pi-exclamation-circle" aria-hidden="true"></i>
												<span>
													{{ message }}
													@if (startEmailTaken()) {
														<a routerLink="/access/candidates">{{ 'merit.onboarding.signup.signIn' | transloco }}</a>
													}
												</span>
											</p>
										}
										@if (starting()) {
											<p class="cv-ai cv-ai--working">
												<app-spinner size="small"></app-spinner>
												{{ 'merit.onboarding.signup.starting' | transloco }}
											</p>
										}

										<!-- Los tres ficheros juntos, arriba (clienta, 23/09): el CV se lee
										     con IA y rellena el resto de pasos. CV y foto obligatorios; el
										     vídeo puede subirse después. -->
										<div class="ob-docs">
											<div class="ob-docs__item">
												<span class="ob-docs__label">
													{{ 'merit.onboarding.documents.cv' | transloco }}<span class="qs-req">*</span>
												</span>
												<app-onboarding-dropzone
													kind="cv"
													[compact]="true"
													[mimeTypes]="cvMime"
													[extensions]="cvExtensions"
													[maxBytes]="cvMaxBytes"
													[current]="data.cv"
													[uploading]="uploadingKind() === 'cv'"
													[serverError]="null"
													(picked)="uploadError.set(null)"
													(accepted)="onUpload('cv', $event)"
												></app-onboarding-dropzone>
												<!-- La IA leyendo el CV: lo que sale de aquí es una propuesta. -->
												@if (cvAnalyzing()) {
													<p class="cv-ai cv-ai--working">
														<app-spinner size="small"></app-spinner>
														{{ 'merit.onboarding.cv.analyzing' | transloco }}
													</p>
												} @else if (cvExtraction(); as extraction) {
													@switch (extraction.status) {
														@case ('ok') {
															<p class="cv-ai cv-ai--done">{{ 'merit.onboarding.cv.filled' | transloco }}</p>
														}
														@case ('partial') {
															<p class="cv-ai cv-ai--done">
																{{ 'merit.onboarding.cv.filledPartial' | transloco }}
															</p>
														}
														@case ('error') {
															<p class="cv-ai cv-ai--failed">
																{{ (extraction.errorKey ?? 'merit.onboarding.cv.errors.AI_FAILED') | transloco }}
															</p>
														}
													}
												}
											</div>
											<div class="ob-docs__item">
												<span class="ob-docs__label">
													{{ 'merit.onboarding.documents.photo' | transloco }}<span class="qs-req">*</span>
												</span>
												<app-onboarding-dropzone
													kind="photo"
													[compact]="true"
													[mimeTypes]="photoMime"
													[maxBytes]="photoMaxBytes"
													[current]="data.photo"
													[uploading]="uploadingKind() === 'photo'"
													[serverError]="null"
													(picked)="uploadError.set(null)"
													(accepted)="onUpload('photo', $event)"
												></app-onboarding-dropzone>
											</div>
											<div class="ob-docs__item">
												<span class="ob-docs__label">
													{{ 'merit.onboarding.documents.video' | transloco }}
													<span class="ob-docs__optional">({{ 'merit.onboarding.documents.optional' | transloco }})</span>
												</span>
												<app-onboarding-dropzone
													kind="video"
													[compact]="true"
													[mimeTypes]="videoMime"
													[maxBytes]="videoMaxBytes"
													[minSeconds]="videoMinSeconds"
													[maxSeconds]="videoMaxSeconds"
													[current]="data.video"
													[uploading]="uploadingKind() === 'video'"
													[serverError]="null"
													(picked)="uploadError.set(null)"
													(accepted)="onUpload('video', $event)"
												></app-onboarding-dropzone>
											</div>
										</div>
										<!-- Un solo sitio para el error del servidor: con tres zonas, pintarlo
										     en la del CV hacía creer que había fallado el CV. -->
										@if (uploadError(); as message) {
											<p class="qs-alert" role="alert">
												<i class="pi pi-exclamation-circle" aria-hidden="true"></i>
												<span>{{ message }}</span>
											</p>
										}
									}

									@case ('experience') {
										<!-- Nivel de gestión (23/09): lo pedía el formulario corto y lo
										     puntúa el K-Score. Sale de los títulos del CV si los hay. -->
										<div class="qs-grid ob-entries__meta" formGroupName="experienceInfo">
											<div class="qs-field qs-col-6">
												<label class="qs-label">
													{{ 'merit.onboarding.fields.managementExperience' | transloco }}<span class="qs-req">*</span>
													@if (isSuggested('experienceInfo.managementExperience')) {
														<span class="cv-tag">{{ 'merit.onboarding.cv.suggested' | transloco }}</span>
													}
												</label>
												<app-select
													[options]="managementOptions"
													formControlName="managementExperience"
													[error]="showErr('experienceInfo.managementExperience')"
													[appendTo]="null"
													size="medium"
													[placeholder]="'merit.onboarding.placeholders.select' | transloco"
												></app-select>
											</div>
										</div>
										<div class="ob-entries" formArrayName="experience">
											@for (entry of experience.controls; track entry; let i = $index) {
												<div class="ob-entry" [formGroupName]="i">
													<div class="ob-entry__head">
														<span class="ob-entry__title">
															{{ 'merit.onboarding.entries.experience' | transloco: { n: i + 1 } }}
															@if (isSuggested('experience.' + i)) {
																<span class="cv-tag">{{ 'merit.onboarding.cv.suggested' | transloco }}</span>
															}
														</span>
														<button
															type="button"
															class="ob-entry__remove"
															(click)="removeEntry(experience, i)"
														>
															<lucide-icon name="Trash2" [size]="14"></lucide-icon>
															{{ 'merit.onboarding.actions.remove' | transloco }}
														</button>
													</div>
													<div class="qs-grid">
														<div class="qs-field qs-col-4">
															<label class="qs-label">
																{{ 'merit.onboarding.fields.position' | transloco }}<span class="qs-req">*</span>
															</label>
															<app-select
																[options]="positionOptions"
																formControlName="position"
																[error]="showErr('experience.' + i + '.position')"
																[filter]="true"
																[appendTo]="null"
																size="medium"
																[placeholder]="'merit.onboarding.placeholders.select' | transloco"
															></app-select>
														</div>
														<div class="qs-field qs-col-4">
															<label class="qs-label">
																{{ 'merit.onboarding.fields.department' | transloco }}<span class="qs-req">*</span>
															</label>
															<app-select
																[options]="departmentOptions"
																formControlName="department"
																[error]="showErr('experience.' + i + '.department')"
																[appendTo]="null"
																size="medium"
																[placeholder]="'merit.onboarding.placeholders.select' | transloco"
															></app-select>
														</div>
														<div class="qs-field qs-col-4">
															<label class="qs-label">
																{{ 'merit.onboarding.fields.employerProperty' | transloco }}<span class="qs-req">*</span>
															</label>
															<input
																class="qs-input"
																[class.is-invalid]="showErr('experience.' + i + '.employerProperty')"
																formControlName="employerProperty"
															/>
														</div>
														<div class="qs-field qs-col-4">
															<label class="qs-label">
																{{ 'merit.onboarding.fields.luxuryLevel' | transloco }}<span class="qs-req">*</span>
															</label>
															<app-select
																[options]="luxuryOptions"
																formControlName="luxuryLevel"
																[error]="showErr('experience.' + i + '.luxuryLevel')"
																[appendTo]="null"
																size="medium"
																[placeholder]="'merit.onboarding.placeholders.select' | transloco"
															></app-select>
														</div>
														<div class="qs-field qs-col-12">
															<span class="qs-hint">
																{{ 'merit.onboarding.fields.jobCountryHint' | transloco }}
															</span>
														</div>
														<app-country-select
															formControlName="countryCode"
															[error]="showErr('experience.' + i + '.countryCode')"
															[appendTo]="null"
															size="medium"
															continentFieldClass="qs-field qs-col-6"
															countryFieldClass="qs-field qs-col-6"
														></app-country-select>
														<div class="qs-field qs-col-4">
															<label class="qs-label">
																{{ 'merit.onboarding.fields.fromDate' | transloco }}<span class="qs-req">*</span>
															</label>
															<input
																type="date"
																class="qs-input"
																[class.is-invalid]="showErr('experience.' + i + '.fromDate')"
																formControlName="fromDate"
															/>
														</div>
														<div class="qs-field qs-col-4">
															<label class="qs-label">
																{{ 'merit.onboarding.fields.toDate' | transloco }}
															</label>
															<input type="date" class="qs-input" formControlName="toDate" />
															<span class="qs-hint">
																{{ 'merit.onboarding.fields.toDateHint' | transloco }}
															</span>
														</div>
														<div class="qs-field qs-col-12">
															<label class="qs-label">
																{{ 'merit.onboarding.fields.description' | transloco }}
															</label>
															<textarea
																class="qs-input qs-textarea"
																rows="3"
																formControlName="description"
															></textarea>
														</div>
													</div>
												</div>
											} @empty {
												<p class="ob-empty">{{ 'merit.onboarding.empty.experience' | transloco }}</p>
											}
											<button type="button" class="ob-add" (click)="addExperience()">
												<lucide-icon name="Plus" [size]="15"></lucide-icon>
												{{ 'merit.onboarding.actions.addExperience' | transloco }}
											</button>
										</div>
									}

									@case ('education') {
										<div class="ob-entries" formArrayName="education">
											@for (entry of education.controls; track entry; let i = $index) {
												<div class="ob-entry" [formGroupName]="i">
													<div class="ob-entry__head">
														<span class="ob-entry__title">
															{{ 'merit.onboarding.entries.education' | transloco: { n: i + 1 } }}
															@if (isSuggested('education.' + i)) {
																<span class="cv-tag">{{ 'merit.onboarding.cv.suggested' | transloco }}</span>
															}
														</span>
														<button
															type="button"
															class="ob-entry__remove"
															(click)="removeEntry(education, i)"
														>
															<lucide-icon name="Trash2" [size]="14"></lucide-icon>
															{{ 'merit.onboarding.actions.remove' | transloco }}
														</button>
													</div>
													<div class="qs-grid">
														<div class="qs-field qs-col-4">
															<label class="qs-label">
																{{ 'merit.onboarding.fields.level' | transloco }}<span class="qs-req">*</span>
															</label>
															<app-select
																[options]="educationLevelOptions"
																formControlName="level"
																[error]="showErr('education.' + i + '.level')"
																[appendTo]="null"
																size="medium"
																[placeholder]="'merit.onboarding.placeholders.select' | transloco"
															></app-select>
														</div>
														<div class="qs-field qs-col-5">
															<label class="qs-label">
																{{ 'merit.onboarding.fields.degreeTitle' | transloco }}<span class="qs-req">*</span>
															</label>
															<input
																class="qs-input"
																[class.is-invalid]="showErr('education.' + i + '.title')"
																formControlName="title"
															/>
														</div>
														<div class="qs-field qs-col-3">
															<label class="qs-label">
																{{ 'merit.onboarding.fields.year' | transloco }}<span class="qs-req">*</span>
															</label>
															<input
																type="number"
																class="qs-input"
																[class.is-invalid]="showErr('education.' + i + '.year')"
																formControlName="year"
																min="1950"
																max="2100"
															/>
														</div>
														<div class="qs-field qs-col-6">
															<label class="qs-label">
																{{ 'merit.onboarding.fields.institution' | transloco }}<span class="qs-req">*</span>
															</label>
															<input
																class="qs-input"
																[class.is-invalid]="showErr('education.' + i + '.institution')"
																formControlName="institution"
															/>
														</div>
														<div class="qs-field qs-col-6">
															<label class="qs-label">&nbsp;</label>
															<label class="qs-pill">
																<input type="checkbox" formControlName="isCertification" />
																{{ 'merit.onboarding.fields.isCertification' | transloco }}
															</label>
														</div>
													</div>
												</div>
											} @empty {
												<p class="ob-empty">{{ 'merit.onboarding.empty.education' | transloco }}</p>
											}
											<button type="button" class="ob-add" (click)="addEducation()">
												<lucide-icon name="Plus" [size]="15"></lucide-icon>
												{{ 'merit.onboarding.actions.addEducation' | transloco }}
											</button>
										</div>
									}

									@case ('languages') {
										<div class="ob-entries" formArrayName="languages">
											@for (entry of languages.controls; track entry; let i = $index) {
												<div class="ob-entry" [formGroupName]="i">
													<div class="ob-entry__head">
														<span class="ob-entry__title">
															{{ 'merit.onboarding.entries.language' | transloco: { n: i + 1 } }}
															@if (isSuggested('languages.' + i)) {
																<span class="cv-tag">{{ 'merit.onboarding.cv.suggested' | transloco }}</span>
															}
														</span>
														<button
															type="button"
															class="ob-entry__remove"
															(click)="removeEntry(languages, i)"
														>
															<lucide-icon name="Trash2" [size]="14"></lucide-icon>
															{{ 'merit.onboarding.actions.remove' | transloco }}
														</button>
													</div>
													<div class="qs-grid">
														<div class="qs-field qs-col-5">
															<label class="qs-label">
																{{ 'merit.onboarding.fields.language' | transloco }}<span class="qs-req">*</span>
															</label>
															<app-select
																[options]="languageOptions"
																formControlName="language"
																[error]="showErr('languages.' + i + '.language')"
																[filter]="true"
																[appendTo]="null"
																size="medium"
																[placeholder]="'merit.onboarding.placeholders.select' | transloco"
															></app-select>
														</div>
														<div class="qs-field qs-col-4">
															<label class="qs-label">
																{{ 'merit.onboarding.fields.languageLevel' | transloco }}<span class="qs-req">*</span>
															</label>
															<app-select
																[options]="languageLevelOptions"
																formControlName="cefr"
																[error]="showErr('languages.' + i + '.cefr')"
																[appendTo]="null"
																size="medium"
																[placeholder]="'merit.onboarding.placeholders.select' | transloco"
															></app-select>
														</div>
														<div class="qs-field qs-col-3">
															<label class="qs-label">
																{{ 'merit.onboarding.fields.percent' | transloco }}
															</label>
															<input
																type="number"
																class="qs-input"
																formControlName="percent"
																min="0"
																max="100"
															/>
														</div>
													</div>
												</div>
											} @empty {
												<p class="ob-empty">{{ 'merit.onboarding.empty.languages' | transloco }}</p>
											}
											<button type="button" class="ob-add" (click)="addLanguage()">
												<lucide-icon name="Plus" [size]="15"></lucide-icon>
												{{ 'merit.onboarding.actions.addLanguage' | transloco }}
											</button>
										</div>
									}

									@case ('legal') {
										<div class="qs-grid" formGroupName="legal">
											<div class="qs-field qs-col-6">
												<label class="qs-label">
													{{ 'merit.onboarding.fields.visaStatus' | transloco }}<span class="qs-req">*</span>
												</label>
												<app-select
													[options]="visaOptions"
													formControlName="visaStatus"
													[error]="showErr('legal.visaStatus')"
													[appendTo]="null"
													size="medium"
													[placeholder]="'merit.onboarding.placeholders.select' | transloco"
												></app-select>
											</div>
											<div class="qs-field qs-col-6">
												<label class="qs-label" for="ob-emiratesId">
													{{ 'merit.onboarding.fields.emiratesId' | transloco }}
												</label>
												<input id="ob-emiratesId" class="qs-input" formControlName="emiratesId" />
											</div>
											<div class="qs-field qs-col-6">
												<label class="qs-label" for="ob-currentEmployment">
													{{ 'merit.onboarding.fields.currentEmployment' | transloco }}
												</label>
												<input
													id="ob-currentEmployment"
													class="qs-input"
													formControlName="currentEmployment"
												/>
											</div>
											<div class="qs-field qs-col-6">
												<label class="qs-label">&nbsp;</label>
												<label class="qs-pill">
													<input type="checkbox" formControlName="needsSponsor" />
													{{ 'merit.onboarding.fields.needsSponsor' | transloco }}
												</label>
											</div>
											<div class="qs-field qs-col-6">
												<label class="qs-label">
													{{ 'merit.onboarding.fields.drivingLicence' | transloco }}<span class="qs-req">*</span>
													@if (isSuggested('legal.drivingLicence')) {
														<span class="cv-tag">{{ 'merit.onboarding.cv.suggested' | transloco }}</span>
													}
												</label>
												<app-select
													[options]="drivingOptions"
													formControlName="drivingLicence"
													[error]="showErr('legal.drivingLicence')"
													[appendTo]="null"
													size="medium"
													[placeholder]="'merit.onboarding.placeholders.select' | transloco"
												></app-select>
											</div>
										</div>
									}

									@case ('availability') {
										<div class="qs-grid" formGroupName="availability">
											<div class="qs-field qs-col-6">
												<label class="qs-label">
													{{ 'merit.onboarding.fields.availability' | transloco }}<span class="qs-req">*</span>
												</label>
												<app-select
													[options]="availabilityOptions"
													formControlName="availability"
													[error]="showErr('availability.availability')"
													[appendTo]="null"
													size="medium"
													[placeholder]="'merit.onboarding.placeholders.select' | transloco"
												></app-select>
											</div>
											<div class="qs-field qs-col-6">
												<label class="qs-label">
													{{ 'merit.onboarding.fields.relocation' | transloco }}<span class="qs-req">*</span>
												</label>
												<app-select
													[options]="relocationOptions"
													formControlName="relocation"
													[error]="showErr('availability.relocation')"
													[appendTo]="null"
													size="medium"
													[placeholder]="'merit.onboarding.placeholders.select' | transloco"
												></app-select>
											</div>
											<div class="qs-field qs-col-6">
												<label class="qs-label">
													{{ 'merit.onboarding.fields.gccExperience' | transloco }}<span class="qs-req">*</span>
												</label>
												<app-select
													[options]="gccOptions"
													formControlName="gccExperience"
													[error]="showErr('availability.gccExperience')"
													[appendTo]="null"
													size="medium"
													[placeholder]="'merit.onboarding.placeholders.select' | transloco"
												></app-select>
											</div>
											<div class="qs-field qs-col-12">
												<label class="qs-label">
													{{ 'merit.onboarding.fields.locationTargeting' | transloco }}<span class="qs-req">*</span>
												</label>
												<app-multiselect
													[options]="locationOptions"
													formControlName="locationTargeting"
													[error]="showErr('availability.locationTargeting')"
													[filter]="true"
													size="medium"
													[placeholder]="'merit.onboarding.placeholders.select' | transloco"
												></app-multiselect>
												<span class="qs-hint">{{ 'merit.onboarding.fields.locationTargetingHint' | transloco }}</span>
											</div>
											<!-- El tramo (23/09), no un número: es lo que cruzan el K-Score y
											     el matching, y lo que pedía el formulario corto. -->
											<div class="qs-field qs-col-6">
												<label class="qs-label">
													{{ 'merit.onboarding.fields.salaryExpectation' | transloco }}<span class="qs-req">*</span>
													@if (isSuggested('availability.salaryExpectation')) {
														<span class="cv-tag">{{ 'merit.onboarding.cv.suggested' | transloco }}</span>
													}
												</label>
												<app-select
													[options]="salaryOptions"
													formControlName="salaryExpectation"
													[error]="showErr('availability.salaryExpectation')"
													[appendTo]="null"
													size="medium"
													[placeholder]="'merit.onboarding.placeholders.select' | transloco"
												></app-select>
											</div>
											<div class="qs-field qs-col-6">
												<label class="qs-label" for="ob-notice">
													{{ 'merit.onboarding.fields.noticePeriodDays' | transloco }}
												</label>
												<input
													id="ob-notice"
													type="number"
													class="qs-input"
													formControlName="noticePeriodDays"
													min="0"
													max="365"
												/>
												<span class="qs-hint">{{ 'merit.onboarding.fields.noticePeriodDaysHint' | transloco }}</span>
											</div>
										</div>
									}

									@case ('preferences') {
										<div class="qs-grid" formGroupName="preferences">
											<div class="qs-field qs-col-6">
												<label class="qs-label">
													{{ 'merit.onboarding.fields.targetPositions' | transloco }}<span class="qs-req">*</span>
												</label>
												<app-multiselect
													[options]="positionOptions"
													formControlName="targetPositions"
													[error]="showErr('preferences.targetPositions')"
													[errorMessage]="errMsg('preferences.targetPositions')"
													[filter]="true"
													size="medium"
													[placeholder]="'merit.onboarding.placeholders.select' | transloco"
												></app-multiselect>
												<span class="qs-hint">
													{{ 'merit.onboarding.fields.targetPositionsHint' | transloco }}
												</span>
											</div>
											<!-- Puesto fuera del catálogo (03/09 · 31): se escribe aquí y
											     Kairos lo normaliza desde el panel. -->
											@if (form.controls.preferences.controls.targetPositions.value?.includes('Other')) {
												<div class="qs-field qs-col-6">
													<label class="qs-label" for="targetPositionOther">
														{{ 'merit.onboarding.fields.targetPositionOther' | transloco }}<span class="qs-req">*</span>
													</label>
													<input
														id="targetPositionOther"
														class="qs-input"
														type="text"
														maxlength="120"
														formControlName="targetPositionOther"
														[placeholder]="'merit.onboarding.placeholders.targetPositionOther' | transloco" />
													<span class="qs-hint">{{ 'merit.onboarding.fields.targetPositionOtherHint' | transloco }}</span>
												</div>
											}
											<div class="qs-field qs-col-6">
												<label class="qs-label">
													{{ 'merit.onboarding.fields.propertyTypes' | transloco }}
												</label>
												<app-multiselect
													[options]="propertyTypeOptions"
													formControlName="propertyTypes"
													[filter]="true"
													size="medium"
													[placeholder]="'merit.onboarding.placeholders.select' | transloco"
												></app-multiselect>
											</div>
											<div class="qs-field qs-col-6">
												<label class="qs-label" for="ob-years-role">
													{{ 'merit.onboarding.fields.yearsInTargetRole' | transloco }}
												</label>
												<input
													id="ob-years-role"
													type="number"
													class="qs-input"
													formControlName="yearsInTargetRole"
													min="0"
													max="60"
												/>
												<span class="qs-hint">{{ 'merit.onboarding.fields.yearsInTargetRoleHint' | transloco }}</span>
											</div>
											<div class="qs-field qs-col-6">
												<label class="qs-label">
													{{ 'merit.onboarding.fields.systems' | transloco }}
												</label>
												<app-multiselect
													[options]="systemsOptions"
													formControlName="systems"
													[filter]="true"
													size="medium"
													[placeholder]="'merit.onboarding.placeholders.select' | transloco"
												></app-multiselect>
											</div>
											<!-- Matrix §4: specializations. Filterable instead of grouped
											     by area until a positions→area mapping exists. -->
											<div class="qs-field qs-col-6">
												<label class="qs-label">
													{{ 'merit.onboarding.fields.specializations' | transloco }}
												</label>
												<app-multiselect
													[options]="specializationsOptions"
													formControlName="specializations"
													[filter]="true"
													size="medium"
													[placeholder]="'merit.onboarding.placeholders.select' | transloco"
												></app-multiselect>
												<span class="qs-hint">{{ 'merit.onboarding.fields.specializationsHint' | transloco }}</span>
											</div>
											<!-- Habilidades: texto libre con chips, a diferencia de los
											     dos de arriba. No tienen catálogo ni entran en el
											     matching — se declaran y se enseñan. -->
											<div class="qs-field qs-col-12">
												<label class="qs-label">
													{{ 'merit.onboarding.fields.skills' | transloco }}
												</label>
												<app-chips-input
													formControlName="skills"
													size="medium"
													[placeholder]="'merit.onboarding.placeholders.skills' | transloco"
												></app-chips-input>
												<span class="qs-hint">{{ 'merit.onboarding.fields.skillsHint' | transloco }}</span>
											</div>
										</div>
									}
								}
							</section>

							<div class="qs-actionbar">
								<span class="qs-actionbar__note">
									{{
										'merit.onboarding.progress.stepOf'
											| transloco: { current: index() + 1, total: steps.length }
									}}
								</span>
								<div class="qs-actionbar__buttons">
									<button
										type="button"
										class="qs-btn qs-btn--ghost"
										[disabled]="index() === 0 || saving()"
										(click)="back()"
									>
										{{ 'merit.onboarding.actions.back' | transloco }}
									</button>
									<button type="submit" class="qs-btn qs-btn--primary" [disabled]="saving()">
										{{ nextLabel() | transloco }}
									</button>
								</div>
							</div>
						</form>
					}
				</div>
			</div>
		}
	</div>

	@if (isPublic()) {
		<footer class="ob-footer">
			<div class="ob-footer__brand">KAIROS MERIT</div>
			<div class="ob-footer__tagline">The Right Time for Talent.</div>
			<div class="ob-footer__legal">Kairos Merit Staffing Solutions — FZCO · Dubai, United Arab Emirates</div>
		</footer>
	}
</div>
